import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useDebounced } from '@/lib/useDebounced';
import { Trash2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateAfterEdit } from '@/lib/invalidate';
import { ALL_GRAPHS, GraphSelector, graphForKind, graphDisplayName } from '@/components/GraphSelector';
import { GraphMultiPicker } from '@/components/GraphMultiPicker';
import { hitsWithinScope, scopeNarrowsHits, useGraphScope } from '@/lib/useGraphScope';
import { VdbBuildingNotice } from '@/components/VdbBuildingNotice';
import {
  useEntityIn,
  useEntityReferences,
  useEntityInboundSummaryIn,
  useInboundSubjects,
  mintEntity,
  addStatement,
  retractStatement,
  updateStatementTags,
  deleteEntity,
  mergeEntity,
  setEntityClasses,
  type PredicateValue,
  type UriLabel,
  type TermInput,
  type PropertyInputDto,
  type ValidityView,
  type EntityResponse,
} from '@/lib/useEntity';
import { canImportData, isQros, useMe } from '@/lib/useMe';
import { EditModeButton } from '@/lib/editMode';
import {
  searchTermFromInput,
  useVdbSearch,
  type VdbSearchResult,
} from '@/lib/useVdb';
import { cn } from '@/lib/utils';
import { EntityPicker } from '@/components/EntityPicker';
import { StatementValueInput } from '@/components/StatementValueInput';
import { EMPTY_THING, isTypePredicate, objStateToTerm, type ObjState } from '@/lib/statementValue';
import {
  TagEditor,
  TagChips,
  TagNameChips,
  EMPTY_TAG_DRAFT,
  serializeTags,
  unpickedContext,
  tagViewsToDraft,
  type TagDraft,
} from '@/components/TagEditor';
import { languageTag } from '@/lib/tags';
import { Message, messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

/**
 * Entity Info page — generic v1.
 *
 * Loads a structured payload for any URI in any graph and renders it
 * as a sectioned page: header card (label + types + URI), outbound
 * properties grouped by predicate, inbound references summarized by
 * predicate with sample subjects. URI values throughout are
 * clickable and navigate to that entity's own page within the same
 * graph.
 *
 * Per the parity-plan decision (memory `project_entity_info_pages_cloud.md`,
 * 2026-05-10): SPA-first, generic ahead of class-specific templates.
 * Class-templated overlays land as a follow-up phase.
 */
export function EntityInfoPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const uriParam = params.get('uri');

  const goTo = (uri: string) => {
    const next = new URLSearchParams();
    next.set('uri', uri);
    navigate({ search: next.toString() });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-tres-text-primary">Entity Search and Editing</h1>
        <p className="text-sm text-tres-text-secondary">
          Find any entity; the page shows everything the database knows about it. Click any
          linked entity to open its own page. Pick a specific graph to add or remove statements.
        </p>
      </header>

      <EditableEntity
        uri={uriParam}
        onNavigate={goTo}
        graphStorageKey="graphMultiPicker.entity"
        enableNewInstance
        emptyState="Search above for an entity to view its page."
        toolbarExtras={(graphs) => (
          <div className="flex-1 min-w-[260px]">
            <EntitySearch graphs={graphs} onPick={(r) => goTo(r.uri)} />
          </div>
        )}
      />
    </div>
  );
}

/**
 * Editable entity view — the shared read+write surface behind both the Entity
 * Info page and the Catalog detail pane. Owns the graph scope, the edit-mode
 * gate, and the add/remove/label authoring forms, so there is ONE edit pathway
 * (one gate set, one VDB-rebuild-on-write) with no drift between surfaces.
 *
 * Reads take a SET of graphs and default to the union view (read-only).
 * Authoring resolves to exactly ONE graph — writes can't target the union, and
 * scoping the display to that one graph keeps remove unambiguous
 * (graph-selector-is-the-contract). Navigation is delegated to the host page
 * via `onNavigate` so each surface keeps its own selection semantics.
 */
const UNION_SCOPE: string[] = [ALL_GRAPHS];
const noop = () => {};

export function EditableEntity({
  uri,
  onNavigate,
  toolbarExtras,
  emptyState = 'Select an entity to view its details.',
  initialGraph = ALL_GRAPHS,
  graphStorageKey,
  enableNewInstance = false,
  hideGraphSelector = false,
}: {
  uri: string | null;
  onNavigate: (uri: string) => void;
  /** Rendered in the toolbar, given the live scope — the Entity page's search
   *  box narrows its hits to the same set the detail view reads. */
  toolbarExtras?: (graphs: string[]) => ReactNode;
  emptyState?: string;
  initialGraph?: string;
  graphStorageKey: string;
  enableNewInstance?: boolean;
  // Hiding the selector relies on the home-graph auto-shift below to land on a
  // concrete graph — without it, Edit Mode would have no writable scope.
  hideGraphSelector?: boolean;
}) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const me = useMe();
  const queryClient = useQueryClient();

  // Read scope (a SET). The entity endpoint takes one graph, so a scope of
  // exactly one graph reads that graph and anything wider reads the union — a
  // superset of the selection, never an arbitrary member of it.
  const [storedGraphs, setStoredGraphs] = useGraphScope(graphStorageKey, [initialGraph]);
  // With no picker on screen (the Catalog detail pane) there is nothing to
  // change, so the scope stays the union and the home-graph shift below is what
  // lands the write target — a persisted narrow scope would otherwise strand
  // the pane on one graph with no way back.
  const graphs = hideGraphSelector ? UNION_SCOPE : storedGraphs;
  const setGraphs = hideGraphSelector ? noop : setStoredGraphs;
  const singleScope = graphs.length === 1 && graphs[0] !== ALL_GRAPHS ? graphs[0]! : null;
  const unionScope = graphs.includes(ALL_GRAPHS) || graphs.length === 0;
  // The entity endpoint reads exactly one graph, so a scope naming several
  // concrete graphs is read as the union — WIDER than the selection, which the
  // banner below states. It is never narrowed to one member of the set.
  const widerThanScope = !singleScope && !unionScope;
  // Where the home-graph shift (or a chip) landed. Only consulted under a union
  // scope: a scope of one graph IS the write target, and a multi-graph scope
  // has no unambiguous one.
  const [homeChoice, setHomeChoice] = useState<string | null>(null);
  const writeGraph = singleScope ?? (unionScope ? homeChoice : null);
  const concreteGraph = writeGraph;
  const [editMode, setEditMode] = useState(false);
  const canAuthor = canImportData(me.data) && editMode;
  const canEdit = canAuthor && Boolean(concreteGraph) && Boolean(token);
  // QROS-native statement tags — the RDF version surfaces none.
  const qros = isQros(me.data);

  const [showNew, setShowNew] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Reads follow the write target when there is one, so what is displayed is
  // exactly what a remove can address; otherwise the picked graphs, each read
  // and merged, or the union when that is the pick (74.3).
  const readGraphs = useMemo(
    () => (writeGraph ? [writeGraph] : unionScope ? [ALL_GRAPHS] : graphs),
    [writeGraph, unionScope, graphs],
  );
  const query = useEntityIn(uri, readGraphs);

  // The graphs the entity has statements in. Nothing shifts the scope or the
  // write target on its own (74.3): under the union the page is read-only
  // until the user picks a home graph from the chips below, and that press is
  // the selection.
  const homeGraphs = useMemo(() => query.data?.home_graphs ?? [], [query.data]);

  // The entity exists, but not in the graph on screen. Identity (label, URI,
  // classes) resolves across graphs while writes land in the selected one, so
  // the normal editors here would show a populated Label field whose Update
  // button MINTS that label into a graph the entity does not live in —
  // duplicating it across graphs, which graph-as-identity exists to prevent.
  // Answer "not found here" and make adding it deliberate.
  const absentFromGraph =
    Boolean(concreteGraph) && homeGraphs.length > 0 && !homeGraphs.includes(concreteGraph!);
  const [authorHere, setAuthorHere] = useState(false);
  useEffect(() => {
    setAuthorHere(false);
  }, [uri, concreteGraph]);

  // Pick a home graph explicitly: it becomes both the read scope and the write
  // target, so the two can never disagree.
  const focusGraph = (g: string) => {
    setGraphs([g]);
    setHomeChoice(g);
  };

  // Editing an entity's statements ripples to inbound summaries, the catalog,
  // vocabulary trees and dashboard cards: every data read refreshes, and of the
  // schema reads only the edited graph's (88.13).
  const invalidateEntity = () => {
    invalidateAfterEdit(queryClient, concreteGraph ?? null);
  };

  // A confirmed delete takes the row off the screen AT ONCE, from the cache the
  // page is rendering, and refetches behind that. Waiting for the refetch left
  // the deleted statement on screen — pressing the trash icon again then drew
  // "nothing was deleted", which is how UAT found it (77.1, 2026-09-21). The
  // page must never show a fact the server has just told it is gone.
  const dropFromView = (predicate: UriLabel, value: PredicateValue) => {
    queryClient.setQueryData<EntityResponse>(
      ['entity', uri ?? 'none', (writeGraph ?? ALL_GRAPHS) ?? 'none', token ?? 'anon'],
      (current) =>
        current
          ? {
              ...current,
              outbound: current.outbound
                .map((group) =>
                  group.predicate.uri === predicate.uri
                    ? { ...group, values: group.values.filter((v) => v !== value) }
                    : group,
                )
                .filter((group) => group.values.length > 0),
            }
          : current,
    );
  };

  const handleRemove = async (
    predicate: UriLabel,
    value: PredicateValue,
  ): Promise<string | null> => {
    if (!concreteGraph || !uri || !token) return 'No graph is picked, so there is nothing to write to.';
    const object = valueToTermInput(value);
    if (!object) return 'This value cannot be addressed from this page.';
    setActionError(null);
    const r = await retractStatement(
      {
        graph: concreteGraph,
        subject: uri,
        predicate: predicate.uri,
        object,
        ...(value.statement_id ? { statement_id: value.statement_id } : {}),
      },
      token,
    );
    if (!r.ok && r.error !== 'statement_gone') return r.message;
    dropFromView(predicate, value);
    invalidateEntity();
    if (r.ok) return null;
    // Deleted elsewhere (another tab, another user) since this page was read:
    // the server has said the fact is gone, so the row leaves with the reason.
    const entityName = query.data?.labels.primary ?? uri;
    const valueText =
      value.kind === 'literal' ? ` "${value.value}"` : value.kind === 'uri' ? ` "${value.label}"` : '';
    return `Already deleted: ${predicate.label}${valueText} of ${entityName} was removed from ${graphDisplayName(concreteGraph)} elsewhere, so there was nothing left to delete.`;
  };

  return (
    <div className="space-y-4">
      <VdbBuildingNotice />
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        {toolbarExtras?.(graphs)}
        {!hideGraphSelector && <GraphMultiPicker selected={graphs} onChange={setGraphs} />}
        <EditModeButton on={editMode} onToggle={setEditMode} visible={canImportData(me.data)} />
        {enableNewInstance && canAuthor && (
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className={buttonVariants({ variant: 'primary', size: 'md' })}
          >
            New Instance
          </button>
        )}
        {canEdit && uri && (
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            className={buttonVariants({ variant: 'danger', size: 'md' })}
          >
            Delete…
          </button>
        )}
      </div>

      {/* Home-graph chips: shown whenever the entity lives somewhere other than
          the graph on screen, so a scope that hides its statements is never
          silent. Picking one narrows the scope AND the write target together. */}
      {homeGraphs.some((g) => g !== writeGraph) && !(canAuthor && !concreteGraph) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-tres-text-secondary">
          <span>
            {homeGraphs.length > 1
              ? `This entity has statements in ${homeGraphs.length} graphs — show and edit in:`
              : 'This entity has statements in:'}
          </span>
          {homeGraphs.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => focusGraph(g)}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
              title={g}
            >
              {graphDisplayName(g)}
            </button>
          ))}
        </div>
      )}
      {widerThanScope && (
        <p className="text-xs text-tres-text-secondary">
          Several graphs are picked, so the view reads those graphs together and is read-only.
          Select a single graph to add or remove statements.
        </p>
      )}
      {/* Edit Mode reads ON while all graphs are shown together, which cannot be
          written to; the graph is never switched for the user (74.3), so the
          way in is offered here, at the top of the work. */}
      {canAuthor && !concreteGraph && (
        <Message kind="platform" density="compact" className="flex flex-wrap items-center gap-2">
          {homeGraphs.length > 0 ? (
            <>
              <span>
                Edit Mode is on, but every graph is shown together, so nothing here can be edited.
                To edit, press the graph to edit in:
              </span>
              {homeGraphs.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => focusGraph(g)}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                  title={g}
                >
                  {graphDisplayName(g)}
                </button>
              ))}
            </>
          ) : (
            <span>
              Edit Mode is on, but every graph is shown together, so nothing here can be edited.
              To edit, pick one graph in Graphs above.
            </span>
          )}
        </Message>
      )}
      {canEdit && concreteGraph && (
        <p className="rounded-md border border-tres-success/40 bg-tres-success/5 px-2 py-1 text-xs text-tres-success">
          Editable — changes write to <span className="font-medium">{graphDisplayName(concreteGraph)}</span>.
          Delete a statement with the trash icon beside its value, or add one below.
        </p>
      )}
      {actionError && (
        <div className={messageClass('warning', 'normal')}>
          {actionError}
        </div>
      )}

      {!uri && (
        <div className="rounded-lg border border-dashed border-border bg-card/50 p-8 text-center text-sm text-tres-text-secondary">
          {emptyState}
        </div>
      )}

      {uri && query.isLoading && (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-tres-text-secondary">
          Loading entity…
        </div>
      )}

      {uri && query.error && (
        <div className={messageClass('warning', 'normal')}>
          {(query.error as Error).message}
        </div>
      )}

      {uri && query.data && absentFromGraph && !authorHere && (
        <div className="rounded-lg border border-dashed border-border bg-card/50 p-6 text-center text-sm">
          <p className="font-medium text-tres-text-primary">
            Not found in {graphDisplayName(concreteGraph!)}
          </p>
          <p className="mt-1 text-tres-text-secondary">
            This entity has no statements in this graph. It lives in{' '}
            {homeGraphs.map(graphDisplayName).join(', ')} — pick one above to see and edit it.
          </p>
          <div className="mt-2 break-all rounded bg-muted px-2 py-1 font-mono text-xs text-tres-text-secondary">
            {query.data.uri}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setAuthorHere(true)}
              className="mt-4 rounded-md border border-input px-3 py-1.5 text-sm hover:bg-accent"
            >
              Add it to {graphDisplayName(concreteGraph!)} anyway
            </button>
          )}
        </div>
      )}

      {uri && query.data && (!absentFromGraph || authorHere) && (
        <>
          <EntityView
            data={query.data}
            graph={writeGraph ?? ALL_GRAPHS}
            readGraphs={readGraphs}
            onNavigate={onNavigate}
            onRemoveStatement={canEdit ? handleRemove : undefined}
            tagEditing={
              canEdit && qros && concreteGraph && token
                ? { graph: concreteGraph, token, onDone: invalidateEntity }
                : undefined
            }
          />
          {canEdit && concreteGraph && token && (
            <>
              <LabelEditor
                graph={concreteGraph}
                subject={uri}
                current={query.data.labels.rdfs_label ?? null}
                predicate={qros ? QROS_LABEL : RDFS_LABEL}
                token={token}
                onDone={invalidateEntity}
              />
              <AltLabelsEditor
                graph={concreteGraph}
                subject={uri}
                current={query.data.labels.alt_labels ?? []}
                predicate={qros ? QROS_ALT_LABEL : SKOS_ALT_LABEL}
                token={token}
                onDone={invalidateEntity}
              />
              <ClassEditor
                graph={concreteGraph}
                subject={uri}
                current={query.data.types}
                token={token}
                onDone={invalidateEntity}
              />
              <AddStatementForm
                graph={concreteGraph}
                subject={uri}
                token={token}
                showTags={qros}
                onDone={invalidateEntity}
              />
            </>
          )}
        </>
      )}

      {enableNewInstance && showNew && token && (
        <NewInstanceModal
          token={token}
          showTags={qros}
          onClose={() => setShowNew(false)}
          onCreated={(newUri, createdGraph) => {
            setShowNew(false);
            // Land on the new entity with the creation graph selected, so it's
            // immediately in editable mode (not the read-only union view).
            focusGraph(createdGraph);
            invalidateEntity();
            onNavigate?.(newUri);
          }}
        />
      )}

      {showDelete && concreteGraph && uri && token && query.data && (
        <DeleteEntityDialog
          graph={concreteGraph}
          uri={uri}
          label={query.data.labels.primary}
          token={token}
          onClose={() => setShowDelete(false)}
          onMerged={(target) => {
            setShowDelete(false);
            invalidateEntity();
            onNavigate(target);
          }}
          onDeleted={() => {
            setShowDelete(false);
            invalidateEntity();
            onNavigate('');
          }}
          onChangeLabel={() => {
            setShowDelete(false);
            const box = document.getElementById('entity-label-input');
            box?.scrollIntoView({ block: 'center' });
            box?.focus();
          }}
        />
      )}
    </div>
  );
}

// ---- authoring (create + edit), gated to ImportData on a concrete graph ----

function valueToTermInput(v: PredicateValue): TermInput | null {
  if (v.kind === 'uri') return { uri: v.uri };
  if (v.kind === 'literal') return { value: v.value, lang: v.lang, datatype: v.datatype };
  return null; // bnode — not editable through this surface
}

/** Label predicates by backend. QROS stores labels natively (`:label` /
 *  `:altLabel`); the RDF backend uses rdfs:label / skos:altLabel. The editors
 *  must write the backend's own predicate or the write is a no-op that leaves a
 *  foreign statement behind. */
const RDFS_LABEL = 'http://www.w3.org/2000/01/rdf-schema#label';
export const SKOS_ALT_LABEL = 'http://www.w3.org/2004/02/skos/core#altLabel';
const QROS_LABEL = ':label';
export const QROS_ALT_LABEL = ':altLabel';


/** Dedicated label editor — label is required and common, so it gets its own
 *  box rather than going through the generic predicate search. Writes the
 *  backend's label predicate (`predicate`), replacing any existing one. */
function LabelEditor({
  graph,
  subject,
  current,
  predicate,
  token,
  onDone,
}: {
  graph: string;
  subject: string;
  current: string | null;
  predicate: string;
  token: string;
  onDone: () => void;
}) {
  const [val, setVal] = useState(current ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-sync when navigating between entities or after a save refetch.
  useEffect(() => {
    setVal(current ?? '');
  }, [current, subject]);

  const dirty = val.trim() !== (current ?? '').trim();

  const save = async () => {
    const next = val.trim();
    if (!next) {
      setErr('Label is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    // Replace semantics: retract the existing label (if any) first.
    if (current && current.trim() && current.trim() !== next) {
      const gone = await retractStatement(
        { graph, subject, predicate, object: { value: current } },
        token,
      );
      // Adding the new label over a retract that failed would leave TWO labels.
      if (!gone.ok) {
        setBusy(false);
        return setErr(`The current label was not replaced: ${gone.message}`);
      }
    }
    const r = await addStatement(
      { graph, subject, predicate, object: { value: next } },
      token,
    );
    setBusy(false);
    if (r.ok) onDone();
    else setErr(r.message);
  };

  return (
    <section className="space-y-2 rounded-lg border border-border bg-card p-3">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-tres-text-secondary">
        Label <span className="text-tres-error">*</span>
      </h3>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id="entity-label-input"
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="A human-readable label"
          className="min-w-[240px] flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          type="button"
          disabled={busy || !dirty || !val.trim()}
          onClick={save}
          className={buttonVariants({ variant: 'primary', size: 'md' })}
        >
          {busy ? 'Saving…' : current ? 'Update label' : 'Set label'}
        </button>
      </div>
      {!current && <p className="text-xs text-tres-warning">This entity has no label yet.</p>}
      {err && <p className="text-xs text-tres-error">{err}</p>}
    </section>
  );
}

/** Alternative labels on ANY instance — the model permits them everywhere, not
 *  just vocabulary concepts. Comma-separated field with the vocab editor's
 *  replace-not-append semantics: the saved list REPLACES the whole set (a diff
 *  retracts removed values and adds new ones through the canonical statement
 *  endpoints, like LabelEditor). Empty field clears all alternative labels. */
export function AltLabelsEditor({
  graph,
  subject,
  current,
  predicate,
  token,
  onDone,
}: {
  graph: string;
  subject: string;
  current: string[];
  predicate: string;
  token: string;
  onDone: () => void;
}) {
  const [val, setVal] = useState(current.join(', '));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setVal(current.join(', '));
    // Re-sync on entity navigation or post-save refetch; `current` alone would
    // also fire on refetches that return an identical list, which is harmless.
  }, [current, subject]);

  const parse = (s: string) =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x.length > 0);

  const next = parse(val);
  const dirty =
    next.length !== current.length || next.some((v, i) => v !== current[i]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    const removed = current.filter((v) => !next.includes(v));
    const added = next.filter((v) => !current.includes(v));
    for (const v of removed) {
      const r = await retractStatement(
        { graph, subject, predicate, object: { value: v } },
        token,
      );
      if (!r.ok) {
        setBusy(false);
        setErr(r.message);
        return;
      }
    }
    for (const v of added) {
      const r = await addStatement(
        { graph, subject, predicate, object: { value: v } },
        token,
      );
      if (!r.ok) {
        setBusy(false);
        setErr(r.message);
        return;
      }
    }
    setBusy(false);
    onDone();
  };

  return (
    <section className="space-y-2 rounded-lg border border-border bg-card p-3">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-tres-text-secondary">
        Alternative labels
      </h3>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Comma-separated. Saving replaces the whole set; empty clears."
          className="min-w-[240px] flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={save}
          className={buttonVariants({ variant: 'primary', size: 'md' })}
        >
          {busy ? 'Saving…' : 'Update'}
        </button>
      </div>
      {err && <p className="text-xs text-tres-error">{err}</p>}
    </section>
  );
}

export function AddStatementForm({
  graph,
  subject,
  token,
  showTags,
  onDone,
}: {
  graph: string;
  subject: string;
  token: string;
  showTags: boolean;
  onDone: () => void;
}) {
  const [pred, setPred] = useState<UriLabel | null>(null);
  const [obj, setObj] = useState<ObjState>(EMPTY_THING);
  const [tags, setTags] = useState<TagDraft>(EMPTY_TAG_DRAFT);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const submit = async () => {
    const object = objStateToTerm(obj);
    if (!pred || !object) {
      setErr('Pick a property and give its value.');
      return;
    }
    const pending = showTags ? unpickedContext(tags) : null;
    if (pending) {
      setErr(pending);
      return;
    }
    setBusy(true);
    setErr(null);
    setOutcome(null);
    const drafted = showTags ? serializeTags(tags) : [];
    const lang = obj.mode === 'literal' ? obj.lang : undefined;
    const tagList = showTags ? [...drafted, ...languageTag(lang, drafted)] : [];
    const r = await addStatement(
      { graph, subject, predicate: pred.uri, object, ...(tagList.length ? { tags: tagList } : {}) },
      token,
    );
    setBusy(false);
    if (r.ok) {
      const written = r.data;
      if (written?.already_held) {
        // The statement was already here: its tags merged, no row was added (77.10).
        const changed = written.tags_changed.map((t) => t.replace(/^@/, '')).join(', ');
        setOutcome(
          `This ${pred.label || pred.uri} statement was already held here, so no row was added. ` +
            (changed ? `Its ${changed} changed. ` : 'Nothing about it changed. ') +
            'To hold it in another context, pick a Context in its tags.',
        );
      } else if (written && written.updated > 0) {
        setOutcome(
          `${pred.label || pred.uri} holds one value: the value added replaced the one held before.`,
        );
      }
      setPred(null);
      setObj(EMPTY_THING);
      setTags(EMPTY_TAG_DRAFT);
      onDone();
    } else {
      setErr(r.message);
    }
  };

  return (
    <section className="space-y-2 rounded-lg border border-dashed border-border bg-card/50 p-3">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-tres-text-secondary">
        Add statement
      </h3>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[200px] flex-1">
          <span className="text-xs text-tres-text-secondary">Property</span>
          <EntityPicker
            selected={pred}
            onSelect={(sel) => {
              setPred(sel);
              setObj(EMPTY_THING);
            }}
            placeholder="Search for a property by name…"
            label="Property"
            kind="property"
          />
        </div>
        <div className="min-w-[220px] flex-1">
          <span className="text-xs text-tres-text-secondary">Value</span>
          <StatementValueInput predicate={pred} state={obj} onChange={setObj} />
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className={cn(buttonVariants({ variant: 'primary', size: 'md' }), 'mt-4')}
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
      {showTags && pred && isTypePredicate(pred.uri) && (
        <p className="text-xs text-tres-text-secondary">
          Type statements carry no tags — a thing either is a member of a class or it is not.
        </p>
      )}
      {showTags && !(pred && isTypePredicate(pred.uri)) && (
        <TagEditor
          value={tags}
          onChange={setTags}
          label="Tags for this statement"
          graph={graph}
          predicate={pred?.uri}
          collapsible={false}
        />
      )}
      {err && <p className="text-xs text-tres-error">{err}</p>}
      {outcome && (
        <Message kind="outcome" density="compact">
          {outcome}
        </Message>
      )}
    </section>
  );
}

/** Edit an entity's classes — chip-per-class, add via a class picker, mirroring
 *  the Vocabulary page's multi-class editor. Save diffs the whole `:type` set. */
function ClassEditor({
  graph,
  subject,
  current,
  token,
  onDone,
}: {
  graph: string;
  subject: string;
  current: UriLabel[];
  token: string;
  onDone: () => void;
}) {
  const [classes, setClasses] = useState<UriLabel[]>(current);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-seed when the entity (or its saved types) changes underneath us.
  const currentKey = current
    .map((c) => c.uri)
    .sort()
    .join('|');
  useEffect(() => {
    setClasses(current);
    setErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, currentKey]);

  const editedKey = classes
    .map((c) => c.uri)
    .sort()
    .join('|');
  const dirty = currentKey !== editedKey;

  const add = (sel: UriLabel | null) => {
    if (!sel) return;
    setClasses((prev) => (prev.some((c) => c.uri === sel.uri) ? prev : [...prev, sel]));
  };
  const remove = (u: string) => setClasses((prev) => prev.filter((c) => c.uri !== u));

  const save = async () => {
    if (classes.length === 0) {
      setErr('An entity must have at least one class.');
      return;
    }
    setBusy(true);
    setErr(null);
    const r = await setEntityClasses(
      { graph, uri: subject, classes: classes.map((c) => c.uri) },
      token,
    );
    setBusy(false);
    if (r.ok) onDone();
    else setErr(r.message);
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 text-sm font-medium text-tres-text-primary">Class</div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {classes.map((c) => (
          <span
            key={c.uri}
            className="inline-flex items-center gap-1 rounded-md border border-input bg-muted px-2 py-1 text-sm"
            title={c.uri}
          >
            <span className="truncate">{c.label}</span>
            <button
              type="button"
              onClick={() => remove(c.uri)}
              className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'shrink-0')}
              aria-label={`Remove ${c.label}`}
            >
              ×
            </button>
          </span>
        ))}
        {classes.length === 0 && (
          <span className="text-xs text-tres-error">At least one class is required.</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <EntityPicker selected={null} onSelect={add} placeholder="Add a class…" kind="class" />
        </div>
        <button
          type="button"
          disabled={!dirty || busy || classes.length === 0}
          onClick={save}
          className={buttonVariants({ variant: 'primary', size: 'md' })}
        >
          {busy ? 'Saving…' : 'Save classes'}
        </button>
      </div>
      {err && <p className="mt-2 text-xs text-tres-error">{err}</p>}
    </div>
  );
}

/** Delete-or-merge dialog. An entity other statements link to is not deleted,
 *  since they would be left pointing at nothing: the dialog says how many and
 *  where, and offers a new label or a merge into the entity that replaces it
 *  (77.12). An entity nothing links to deletes with its own statements. */
export function DeleteEntityDialog({
  graph,
  uri,
  label,
  token,
  onClose,
  onMerged,
  onDeleted,
  onChangeLabel,
}: {
  graph: string;
  uri: string;
  label: string;
  token: string;
  onClose: () => void;
  onMerged: (target: string) => void;
  onDeleted: () => void;
  onChangeLabel: () => void;
}) {
  // Merge is substrate-backed on QROS only (global three-position rewrite);
  // on RDF the control is hidden rather than shown-and-erroring.
  const qros = isQros(useMe().data);
  const refs = useEntityReferences(uri);
  const refCount = useMemo(
    () =>
      refs.data ? refs.data.graphs.reduce((n, g) => n + g.count, 0) + refs.data.as_context : 0,
    [refs.data],
  );
  const linksText = useMemo(() => {
    if (!refs.data) return '';
    const parts = refs.data.graphs.map(
      (g) => `${g.count.toLocaleString()} in ${graphDisplayName(g.graph)}`,
    );
    if (refs.data.as_context > 0) {
      parts.push(`${refs.data.as_context.toLocaleString()} using it as their context`);
    }
    return parts.join(', ');
  }, [refs.data]);
  const [target, setTarget] = useState<UriLabel | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doMerge = async () => {
    if (!target) return;
    setBusy(true);
    setErr(null);
    const r = await mergeEntity({ graph, source: uri, target: target.uri }, token);
    setBusy(false);
    if (r.ok) onMerged(target.uri);
    else setErr(r.message);
  };

  const doDelete = async () => {
    setBusy(true);
    setErr(null);
    const r = await deleteEntity({ graph, uri }, token);
    setBusy(false);
    if (!r.ok) return setErr(r.message);
    // A delete the store only partly confirmed is said, not closed over.
    if (r.data.inbound_truncated || r.data.unmatched > 0) {
      return setErr(
        `Deleted ${r.data.removed} statement${r.data.removed === 1 ? '' : 's'}, but not everything: ` +
          (r.data.inbound_truncated
            ? 'this entity is referenced too widely to clear in one pass, so references to it remain. Run Delete again. '
            : '') +
          (r.data.unmatched > 0
            ? `${r.data.unmatched} statement${r.data.unmatched === 1 ? ' was' : 's were'} no longer in ${graphDisplayName(graph)} when it came to remove them.`
            : ''),
      );
    }
    onDeleted();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-tres-text-primary">Delete {label}</h2>
        <p className="mt-1 text-sm text-tres-text-secondary">
          {refs.isLoading
            ? 'Checking what links to it across the database…'
            : refs.error
              ? `Could not check what links to it: ${refs.error.message}`
              : refCount > 0
                ? `${refCount.toLocaleString()}${refs.data?.truncated ? ' or more' : ''} statement${refCount === 1 ? ' links' : 's link'} to ${label} (${linksText}). It cannot be deleted while they do, or they would point at nothing: change its label, or merge it into the entity that replaces it.`
                : 'Nothing links to this entity.'}
        </p>
        {refCount > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={onChangeLabel}
              className={buttonVariants({ variant: 'primary', size: 'md' })}
            >
              Change its label
            </button>
          </div>
        )}

        {qros && (
        <div className="mt-4 rounded-md border border-border p-3">
          <div className="text-sm font-medium text-tres-text-primary">
            Merge into another entity
          </div>
          <p className="mb-2 text-xs text-tres-text-secondary">
            Move every reference to the entity you choose, then delete this one. The target keeps its
            own values and gains this entity&apos;s.
          </p>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <EntityPicker
                selected={target}
                onSelect={setTarget}
                placeholder="Search for the entity to keep…"
              />
            </div>
            <button
              type="button"
              disabled={!target || busy}
              onClick={doMerge}
              className={cn(buttonVariants({ variant: 'primary', size: 'md' }), 'shrink-0')}
            >
              {busy ? 'Merging…' : 'Merge & delete'}
            </button>
          </div>
        </div>
        )}

        {refs.data && refCount === 0 && (
          <div className={cn(messageClass('warning', 'normal'), 'mt-3')}>
            <div className="text-sm font-medium text-tres-error">Delete entity</div>
            <p className="mb-2 text-xs text-tres-text-secondary">
              Removes the entity and its statements. This can&apos;t be undone.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={doDelete}
              className={buttonVariants({ variant: 'danger', size: 'md' })}
            >
              {busy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        )}

        {err && <p className="mt-3 text-sm text-tres-error">{err}</p>}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function NewInstanceModal({
  token,
  showTags,
  onClose,
  onCreated,
}: {
  token: string;
  showTags: boolean;
  onClose: () => void;
  onCreated: (uri: string, graph: string) => void;
}) {
  const [graph, setGraph] = useState<string | null>(graphForKind('data'));
  const [cls, setCls] = useState<UriLabel | null>(null);
  const [label, setLabel] = useState('');
  const [props, setProps] = useState<
    Array<{ pred: UriLabel | null; obj: ObjState; tags: TagDraft }>
  >([{ pred: null, obj: EMPTY_THING, tags: EMPTY_TAG_DRAFT }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Instances are minted as `urn:uuid:…`, so a second "Matthew Petrillo" gets a
  // DIFFERENT address and no uniqueness check anywhere can catch it. The label
  // is the only human-visible handle, so a label collision is the only warning
  // we can give — and it is advisory, not a block: two real people can share a
  // name. Confirm-and-proceed (user ruling 2026-08-03).
  const [dupAcknowledged, setDupAcknowledged] = useState(false);
  const debouncedLabel = useDebounced(label.trim(), 250);
  const labelMatches = useVdbSearch(debouncedLabel, 12, false, 'instance');
  const duplicates = useMemo(
    () =>
      (labelMatches.data ?? []).filter(
        (r: VdbSearchResult) => (r.label ?? '').toLowerCase() === label.trim().toLowerCase(),
      ),
    [labelMatches.data, label],
  );
  useEffect(() => {
    setDupAcknowledged(false);
  }, [debouncedLabel]);

  const setRow = (
    i: number,
    patch: Partial<{ pred: UriLabel | null; obj: ObjState; tags: TagDraft }>,
  ) => setProps((prev) => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const needsDupAck = duplicates.length > 0 && !dupAcknowledged;
  const canSubmit =
    Boolean(graph) && Boolean(cls) && label.trim().length > 0 && !needsDupAck;

  const submit = async () => {
    if (!graph || !cls || !label.trim()) {
      setErr('Pick a graph and a class, and give the instance a label.');
      return;
    }
    const pending = showTags ? props.map((p) => unpickedContext(p.tags)).find((m) => m) : null;
    if (pending) {
      setErr(pending);
      return;
    }
    // Label is required — emitted first as an rdfs:label literal.
    const properties: PropertyInputDto[] = [
      { predicate: RDFS_LABEL, object: { value: label.trim() } },
    ];
    for (const p of props) {
      if (!p.pred) continue;
      const o = objStateToTerm(p.obj);
      if (!o) continue;
      const drafted = showTags ? serializeTags(p.tags) : [];
      const rowLang = p.obj.mode === 'literal' ? p.obj.lang : undefined;
      const rowTags = showTags ? [...drafted, ...languageTag(rowLang, drafted)] : [];
      properties.push({
        predicate: p.pred.uri,
        object: o,
        ...(rowTags.length ? { tags: rowTags } : {}),
      });
    }
    setBusy(true);
    setErr(null);
    const r = await mintEntity({ graph, class: cls.uri, properties }, token);
    setBusy(false);
    if (r.ok) onCreated(r.data.uri, graph);
    else setErr(r.message);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-tres-text-primary">New Instance</h2>
        <div className="mt-3 space-y-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-tres-text-secondary">Graph</span>
            <GraphSelector
              value={graph}
              onChange={setGraph}
              managedOnly
              defaultBare={graphForKind('data')}
              label=""
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-tres-text-secondary">Class</span>
            <EntityPicker
              selected={cls}
              onSelect={setCls}
              placeholder="Search class…"
              kind="class"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-tres-text-secondary">
              Label <span className="text-tres-error">*</span>
            </span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="A human-readable label for this instance"
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {duplicates.length > 0 && (
              <div className={cn(messageClass('warning', 'compact'), 'mt-1')}>
                <p className="font-medium text-tres-error">
                  {duplicates.length === 1
                    ? 'An instance already has this label.'
                    : `${duplicates.length} instances already have this label.`}
                </p>
                <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-tres-text-secondary">
                  {duplicates.slice(0, 5).map((d: VdbSearchResult) => (
                    <li key={d.uri} className="break-all">
                      {d.uri}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-tres-text-secondary">
                  Creating this makes a separate entity with its own address — it will not merge
                  with the one above.
                </p>
                <label className="mt-2 flex items-center gap-1.5 text-tres-text-primary">
                  <input
                    type="checkbox"
                    checked={dupAcknowledged}
                    onChange={(e) => setDupAcknowledged(e.target.checked)}
                  />
                  This is a different thing that happens to share the label
                </label>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <span className="text-xs text-tres-text-secondary">Additional properties</span>
            {props.map((p, i) => (
              <div key={i} className="space-y-2 rounded border border-border p-2">
                <div className="flex flex-wrap items-start gap-2">
                  <div className="min-w-[150px] flex-1">
                    <EntityPicker
                      selected={p.pred}
                      onSelect={(sel) => setRow(i, { pred: sel, obj: EMPTY_THING })}
                      placeholder="Search for a property by name…"
                      label="Property"
                      kind="property"
                    />
                  </div>
                  <div className="min-w-[180px] flex-1">
                    <StatementValueInput
                      predicate={p.pred}
                      state={p.obj}
                      onChange={(s) => setRow(i, { obj: s })}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setProps((prev) => prev.filter((_, j) => j !== i))}
                    className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                    aria-label="Remove property row"
                  >
                    ×
                  </button>
                </div>
                {showTags && (
                  <TagEditor
                    value={p.tags}
                    onChange={(t) => setRow(i, { tags: t })}
                    label="Tags for this statement"
                    graph={graph ?? undefined}
                    predicate={p.pred?.uri}
                  />
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setProps((prev) => [
                  ...prev,
                  { pred: null, obj: EMPTY_THING, tags: EMPTY_TAG_DRAFT },
                ])
              }
              className={buttonVariants({ variant: 'link', size: 'sm' })}
            >
              + add property
            </button>
          </div>
          {err && <p className="text-xs text-tres-error">{err}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !canSubmit}
            onClick={submit}
            className={buttonVariants({ variant: 'primary', size: 'md' })}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- entity view ----

/** Per-statement tag-edit wiring, present only when authoring a QROS graph.
 *  Tags attach to STATEMENTS, never to entities — so the edit affordance
 *  lives on each statement row, with that row as the visible target. */
export interface StatementTagEditing {
  graph: string;
  token: string;
  onDone: () => void;
}

export function EntityView({
  data,
  graph,
  onNavigate,
  onRemoveStatement,
  tagEditing,
  readGraphs,
  embedded = false,
}: {
  data: import('@/lib/useEntity').EntityResponse;
  graph: string | null;
  /** When provided, entity-link clicks call this instead of routing to the
   *  Entity page — used by the viz details pane to re-center the graph. */
  onNavigate?: (uri: string) => void;
  /** When provided (authoring on a concrete graph), each outbound value
   *  gets a remove affordance. Absent → read-only (the viz pane). */
  onRemoveStatement?:
    | ((predicate: UriLabel, value: PredicateValue) => Promise<string | null>)
    | undefined;
  /** When provided (QROS authoring), each statement row gets an Edit-tags
   *  affordance saving through the statement PATCH endpoint. */
  tagEditing?: StatementTagEditing | undefined;
  /** The graphs the host read the entity across; the inbound summary reads
   *  the same set. Defaults to `graph`. */
  readGraphs?: string[];
  /** The host already renders the identity block (label, type, address) above
   *  this view — the Ontology and Vocabulary detail panes do. Suppress ours so
   *  the same facts are not printed twice on one screen. */
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  const goToEntity = (uri: string) => {
    if (onNavigate) {
      onNavigate(uri);
      return;
    }
    const next = new URLSearchParams();
    next.set('uri', uri);
    if (graph) {
      next.set('graph', graph);
    }
    // Embedded in another page, a link opens the term on the Entities page:
    // only that page reads `?uri=`, so changing the host's address alone
    // moved nothing on screen.
    navigate(embedded ? { pathname: '/entity', search: next.toString() } : { search: next.toString() });
  };

  // Inbound ("Referenced by") loads lazily — it's a full-store scan on QROS, so
  // keeping it off the main payload lets the indexed outbound/types render first.
  const inbound = useEntityInboundSummaryIn(data.uri, readGraphs ?? [graph ?? data.graph]);
  const inboundPredicates = inbound.data?.predicates ?? [];
  const inboundTotal = inboundPredicates.reduce((sum, p) => sum + p.count, 0);

  // Pull image values out of their predicate cards into a gallery row
  // below the statements — entities may carry multiple images in future
  // versions, so the row is plural by design. Groups left empty by
  // the extraction are dropped entirely (no bare "hasImage" card).
  const galleryImages: string[] = [];
  const propertyGroups = data.outbound
    .map((group) => ({
      ...group,
      values: group.values.filter((v) => {
        const img = imageUrl(v, group.predicate.uri);
        if (img) galleryImages.push(img);
        return !img;
      }),
    }))
    .filter((g) => g.values.length > 0);

  // A refused delete is printed under its statement's property. The row can
  // leave with the refusal (the fact was already gone), taking the property's
  // last value with it, so the refusal lives here and keeps its card in place.
  const [removeProblem, setRemoveProblem] = useState<{
    entity: string;
    predicate: UriLabel;
    position: number;
    message: string;
  } | null>(null);
  const problem = removeProblem?.entity === data.uri ? removeProblem : null;
  const shownGroups =
    problem && !propertyGroups.some((g) => g.predicate.uri === problem.predicate.uri)
      ? [
          ...propertyGroups.slice(0, problem.position),
          { predicate: problem.predicate, values: [] },
          ...propertyGroups.slice(problem.position),
        ]
      : propertyGroups;

  return (
    <article className="space-y-4">
      {/* Header card — skipped when the host already shows the identity. */}
      {!embedded && (
        <section className="rounded-lg border border-border bg-card p-5">
        <h2 className="text-2xl font-semibold text-tres-text-primary">{data.labels.primary}</h2>
        {data.labels.alt_labels && data.labels.alt_labels.length > 0 && (
          <p className="mt-1 text-sm text-tres-text-secondary">
            Also known as: {data.labels.alt_labels.join(', ')}
          </p>
        )}
        {data.types.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {data.types.map((t) => (
              <span
                key={t.uri}
                title={t.uri}
                className="inline-flex items-center gap-1 rounded-md bg-tres-menu-bg/10 px-2 py-0.5 text-xs font-medium text-tres-menu-bg"
              >
                {t.label}
                <CopyUriButton uri={t.uri} />
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-start gap-1 break-all rounded bg-muted px-2 py-1 font-mono text-xs text-tres-text-secondary">
          <span className="min-w-0">{data.uri}</span>
          <CopyUriButton uri={data.uri} className="ml-auto" />
        </div>
        <div className="mt-2 text-xs text-tres-text-secondary">
          Outbound predicates: {data.outbound.length}
          {inbound.isLoading && ' · Referenced by …'}
          {inbound.data && (
            <>
              {' · Referenced by '}
              {inboundPredicates.length} predicate(s), {inboundTotal} subject(s)
              {inbound.data.truncated && ` (showing first ${inbound.data.rows_seen})`}
            </>
          )}
          </div>
        </section>
      )}

      {/* Statements & Tags — directly after the identity header in read AND
          edit modes (DEV10 item H: the differentiating feature, not buried). */}
      {shownGroups.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-tres-text-secondary">
            Statements &amp; Tags
          </h3>
          {shownGroups.map((group, position) => (
            <OutboundGroup
              key={group.predicate.uri}
              subject={data.uri}
              subjectLabel={data.labels.primary}
              predicate={group.predicate}
              values={group.values}
              onLinkClick={goToEntity}
              onRemove={
                onRemoveStatement
                  ? async (v) => {
                      const message = await onRemoveStatement(group.predicate, v);
                      setRemoveProblem(
                        message
                          ? { entity: data.uri, predicate: group.predicate, position, message }
                          : null,
                      );
                    }
                  : undefined
              }
              removeError={problem?.predicate.uri === group.predicate.uri ? problem.message : null}
              onClearRemoveError={() => setRemoveProblem(null)}
              tagEditing={tagEditing}
            />
          ))}
        </section>
      )}

      {/* Image gallery */}
      {galleryImages.length > 0 && (
        <section className="flex flex-wrap gap-3 rounded-lg border border-border bg-card p-4">
          {galleryImages.map((src) => (
            <a key={src} href={src} target="_blank" rel="noreferrer" title={src}>
              <img
                src={src}
                alt=""
                loading="lazy"
                className="max-h-56 max-w-xs rounded-md border border-border object-contain"
              />
            </a>
          ))}
        </section>
      )}

      {/* Inbound references — loads lazily (full-store scan on QROS). No umbrella
          section header; the recast predicate labels ("Instances 77",
          "Subclasses 3", …) carry the meaning on their own. */}
      {inbound.isLoading && (
        <p className="px-1 text-xs text-tres-text-secondary">Loading references…</p>
      )}
      {inboundPredicates.length > 0 && (
        <section className="space-y-2">
          {inboundPredicates.map((p) => (
            <InboundGroup
              key={p.predicate.uri}
              entityUri={data.uri}
              graph={data.graph}
              predicate={p.predicate}
              count={p.count}
              sample={p.sample}
              onLinkClick={goToEntity}
            />
          ))}
        </section>
      )}

      {galleryImages.length === 0 &&
        propertyGroups.length === 0 &&
        !inbound.isLoading &&
        inboundPredicates.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-card/50 p-6 text-center text-sm text-tres-text-secondary">
            This entity has no recorded properties or references in this graph.
          </div>
        )}
    </article>
  );
}

// ---- predicate groups ----

/** Shows WHY a fact is out of effect today — expired / not-yet-valid — and, when
 * the binding date was INHERITED from the fact's `@context`, names that context as
 * a clickable link. That is the "errant context" audit path: if a fact wrongly
 * dropped out of a current answer, jump straight to the context that bounded it. */
function ValidityBadge({
  validity,
  onLinkClick,
}: {
  validity: ValidityView | undefined;
  onLinkClick: (uri: string) => void;
}) {
  if (!validity || (!validity.expired && !validity.not_yet_valid)) return null;
  const label = validity.expired ? 'Expired' : 'Not yet valid';
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
      <span className="rounded bg-tres-error/15 px-1.5 py-0.5 font-semibold uppercase text-tres-error">
        {label}
      </span>
      {validity.expired && validity.until && (
        <span className="text-tres-text-secondary">as of {validity.until}</span>
      )}
      {validity.via_context && (
        <span className="text-tres-text-secondary">
          via{' '}
          <button
            type="button"
            onClick={() => onLinkClick(validity.via_context!.uri)}
            title={`Validity inherited from context "${validity.via_context.label}" — click to inspect it`}
            className={cn(buttonVariants({ variant: 'link', size: 'md' }), 'text-inherit decoration-dotted')}
          >
            {validity.via_context.label}
          </button>
        </span>
      )}
    </span>
  );
}

const COLLAPSE_THRESHOLD = 10;

/** Inline per-statement tag editor — the ONLY tag-edit surface on this page.
 *  Tags attach to statements, so the editor opens under its statement row,
 *  echoes the target fact, and saves through the statement PATCH endpoint
 *  (retract + re-assert with exactly the submitted tag set). An explicit Save
 *  per statement; failures surface inline. */
function StatementTagsEditor({
  graph,
  subject,
  subjectLabel,
  predicate,
  value,
  token,
  onDone,
  onCancel,
}: {
  graph: string;
  subject: string;
  subjectLabel: string;
  predicate: UriLabel;
  value: PredicateValue;
  token: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<TagDraft>(() => tagViewsToDraft(value.tags ?? []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const baseline = JSON.stringify(serializeTags(tagViewsToDraft(value.tags ?? [])));
  const dirty = JSON.stringify(serializeTags(draft)) !== baseline;

  const objectText =
    value.kind === 'uri' ? value.label : value.kind === 'literal' ? value.value : value.uri;

  const save = async () => {
    const object = valueToTermInput(value);
    if (!object) {
      setErr('This statement cannot be edited here.');
      return;
    }
    const pending = unpickedContext(draft);
    if (pending) {
      setErr(pending);
      return;
    }
    setBusy(true);
    setErr(null);
    const r = await updateStatementTags(
      {
        graph,
        subject,
        predicate: predicate.uri,
        object,
        tags: serializeTags(draft),
        ...(value.statement_id ? { statement_id: value.statement_id } : {}),
      },
      token,
    );
    setBusy(false);
    if (r.ok) onDone();
    else setErr(r.message);
  };

  return (
    <div className="mt-2 space-y-2 rounded-md border border-border bg-card/50 p-2">
      <p className="break-words text-xs text-tres-text-secondary" title={predicate.uri}>
        Tags for:{' '}
        <span className="font-medium text-tres-text-primary">{subjectLabel}</span>
        {' → '}
        <span className="font-medium text-tres-menu-bg">{predicate.label}</span>
        {' → '}
        <span className="font-medium text-tres-text-primary">{objectText}</span>
      </p>
      <TagEditor
        value={draft}
        onChange={setDraft}
        label="Tags on this statement"
        graph={graph}
        predicate={predicate.uri}
        defaultOpen
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={save}
          className={buttonVariants({ variant: 'primary', size: 'md' })}
        >
          {busy ? 'Saving…' : 'Save tags'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className={buttonVariants({ variant: 'secondary', size: 'md' })}
        >
          Cancel
        </button>
        {err && <p className="text-xs text-destructive">{err}</p>}
      </div>
    </div>
  );
}

function OutboundGroup({
  subject,
  subjectLabel,
  predicate,
  values,
  onLinkClick,
  onRemove,
  removeError,
  onClearRemoveError,
  tagEditing,
}: {
  subject: string;
  subjectLabel: string;
  predicate: UriLabel;
  values: PredicateValue[];
  onLinkClick: (uri: string) => void;
  onRemove?: ((value: PredicateValue) => Promise<void>) | undefined;
  removeError: string | null;
  onClearRemoveError: () => void;
  tagEditing?: StatementTagEditing | undefined;
}) {
  const longList = values.length > COLLAPSE_THRESHOLD;
  const [expanded, setExpanded] = useState(!longList);
  const [editingTagsIdx, setEditingTagsIdx] = useState<number | null>(null);
  // Asking, and any refusal, belong beside the statement: the page-level banner
  // sits at the top, out of sight when the list is long (UAT, 2026-09-21).
  const [confirmingIdx, setConfirmingIdx] = useState<number | null>(null);
  const [removing, setRemoving] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card">
      <header
        className={cn(
          'flex items-center gap-2 px-4 py-2',
          longList && 'cursor-pointer hover:bg-muted/50',
        )}
        onClick={longList ? () => setExpanded((e) => !e) : undefined}
      >
        <span className="text-xs text-tres-text-secondary">this entity →</span>
        <span
          className="text-sm font-medium text-tres-menu-bg"
          title={predicate.uri}
        >
          {predicate.label}
        </span>
        <span className="text-xs text-tres-text-secondary">→ …</span>
        {values.length > 1 && (
          <span className="rounded bg-tres-action/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-tres-action">
            {values.length}
          </span>
        )}
        {longList && (
          <span className="ml-auto text-[10px] text-tres-text-secondary">
            {expanded ? 'click to collapse' : 'click to expand'}
          </span>
        )}
      </header>
      {expanded && (
        <ul className="divide-y divide-border">
          {values.map((v, i) => (
            <li key={`${i}-${valueKey(v)}`} className="px-4 py-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="min-w-0 flex-1">
                  <ValueRender value={v} onLinkClick={onLinkClick} />
                  <TagChips tags={v.tags} />
                  {v.kind !== 'bnode' && (
                    <ValidityBadge validity={v.validity} onLinkClick={onLinkClick} />
                  )}
                </span>
                {tagEditing && v.kind !== 'bnode' && !isTypePredicate(predicate.uri) && (
                  <button
                    type="button"
                    onClick={() => setEditingTagsIdx((cur) => (cur === i ? null : i))}
                    title="Edit this statement's tags"
                    className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'shrink-0 text-[11px] leading-5')}
                  >
                    {editingTagsIdx === i ? 'close' : 'edit tags'}
                  </button>
                )}
                {onRemove && v.kind !== 'bnode' && confirmingIdx !== i && (
                  <button
                    type="button"
                    onClick={() => {
                      // Rows are index-keyed; a removal shifts them, so an open
                      // tag editor could silently retarget a different row.
                      setEditingTagsIdx(null);
                      onClearRemoveError();
                      setConfirmingIdx(i);
                    }}
                    title="Delete this statement"
                    aria-label="Delete this statement"
                    className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'shrink-0 leading-none')}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
                {onRemove && confirmingIdx === i && (
                  <span className="flex shrink-0 items-center gap-1 text-xs">
                    <span className="text-tres-text-secondary">Delete?</span>
                    <button
                      type="button"
                      disabled={removing}
                      onClick={async () => {
                        setRemoving(true);
                        await onRemove(v);
                        setRemoving(false);
                        setConfirmingIdx(null);
                      }}
                      className={buttonVariants({ variant: 'danger', size: 'sm' })}
                    >
                      {removing ? 'Deleting…' : 'Yes'}
                    </button>
                    <button
                      type="button"
                      disabled={removing}
                      onClick={() => setConfirmingIdx(null)}
                      className="rounded border border-border px-1.5 py-0.5 hover:bg-muted disabled:opacity-50"
                    >
                      No
                    </button>
                  </span>
                )}
              </div>
              {tagEditing && editingTagsIdx === i && v.kind !== 'bnode' && (
                <StatementTagsEditor
                  graph={tagEditing.graph}
                  subject={subject}
                  subjectLabel={subjectLabel}
                  predicate={predicate}
                  value={v}
                  token={tagEditing.token}
                  onDone={() => {
                    setEditingTagsIdx(null);
                    tagEditing.onDone();
                  }}
                  onCancel={() => setEditingTagsIdx(null)}
                />
              )}
            </li>
          ))}
          {removeError && (
            <li className="px-4 pb-2 text-xs text-tres-error">{removeError}</li>
          )}
        </ul>
      )}
    </div>
  );
}

const INBOUND_PAGE = 25;

function InboundGroup({
  entityUri,
  graph,
  predicate,
  count,
  sample,
  onLinkClick,
}: {
  entityUri: string;
  graph: string;
  predicate: UriLabel;
  count: number;
  sample: UriLabel[];
  onLinkClick: (uri: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [offset, setOffset] = useState(0);
  const page = useInboundSubjects(entityUri, graph, predicate.uri, offset, expanded, INBOUND_PAGE);
  // The summary payload's count is capped server-side; once the paged
  // endpoint answers, its exact total wins.
  const total = page.data?.total ?? count;
  const pageCount = Math.max(1, Math.ceil(total / INBOUND_PAGE));
  const curPage = Math.floor(offset / INBOUND_PAGE);

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-2">
      <header className="flex items-center gap-2">
        <span className="text-xs text-tres-text-secondary">… →</span>
        <span
          className="text-sm font-medium text-tres-menu-bg"
          title={predicate.uri}
        >
          {predicate.label}
        </span>
        <span className="text-xs text-tres-text-secondary">→ this entity</span>
        <span className="rounded bg-tres-action/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-tres-action">
          {total}
        </span>
      </header>
      {!expanded && sample.length > 0 && (
        <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm">
          {sample.map((s) => (
            <SubjectChip key={s.uri} subject={s} onLinkClick={onLinkClick} />
          ))}
          {count > sample.length && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                title="Show all, paged"
              >
                + {count - sample.length} more
              </button>
            </li>
          )}
        </ul>
      )}
      {expanded && (
        <div className="mt-1">
          {page.isLoading && (
            <div className="text-xs text-tres-text-secondary">Loading…</div>
          )}
          {page.error && (
            <div className="text-xs text-tres-error">{(page.error as Error).message}</div>
          )}
          {page.data && (
            <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
              {page.data.items.map((s) => (
                <SubjectChip key={s.uri} subject={s} onLinkClick={onLinkClick} />
              ))}
            </ul>
          )}
          <div className="mt-2 flex items-center justify-end gap-2 text-xs text-tres-text-secondary">
            {pageCount > 1 && (
              <>
                <button
                  type="button"
                  disabled={curPage === 0}
                  onClick={() => setOffset((curPage - 1) * INBOUND_PAGE)}
                  className="rounded border border-input px-1.5 py-0.5 disabled:opacity-40"
                >
                  Prev
                </button>
                <span>
                  page {curPage + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  disabled={curPage >= pageCount - 1}
                  onClick={() => setOffset((curPage + 1) * INBOUND_PAGE)}
                  className="rounded border border-input px-1.5 py-0.5 disabled:opacity-40"
                >
                  Next
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => {
                setExpanded(false);
                setOffset(0);
              }}
              className={buttonVariants({ variant: 'link', size: 'md' })}
            >
              Collapse
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubjectChip({
  subject,
  onLinkClick,
}: {
  subject: UriLabel;
  onLinkClick: (uri: string) => void;
}) {
  return (
    <li className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => onLinkClick(subject.uri)}
        className={buttonVariants({ variant: 'link', size: 'md' })}
        title={subject.uri}
      >
        {subject.label}
      </button>
      <CopyUriButton uri={subject.uri} />
    </li>
  );
}

export function CopyUriButton({ uri, className }: { uri: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(uri).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        });
      }}
      className={cn(
        'inline-flex shrink-0 items-center rounded px-1 text-[11px] leading-none',
        copied
          ? 'text-tres-action'
          : 'text-tres-text-secondary/60 hover:bg-muted hover:text-tres-text-primary',
        className,
      )}
      title={copied ? 'Copied' : `Copy URI\n${uri}`}
      aria-label="Copy URI"
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
}

const IMAGE_PREDICATES = new Set(['http://schema.org/image']);
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg)([/?#]|$)/i;

function imageUrl(value: PredicateValue, predicateUri: string): string | null {
  const raw = value.kind === 'uri' ? value.uri : value.kind === 'literal' ? value.value : null;
  if (!raw || !/^https?:\/\//i.test(raw)) return null;
  if (IMAGE_PREDICATES.has(predicateUri) || IMAGE_EXT.test(raw)) return raw;
  return null;
}

function ValueRender({
  value,
  onLinkClick,
}: {
  value: PredicateValue;
  onLinkClick: (uri: string) => void;
}) {
  if (value.kind === 'uri') {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => onLinkClick(value.uri)}
          className={cn(buttonVariants({ variant: 'link', size: 'md' }), 'text-left')}
          title={value.uri}
        >
          {value.label}
        </button>
        <CopyUriButton uri={value.uri} />
      </span>
    );
  }
  if (value.kind === 'bnode') {
    return <span className="font-mono text-xs text-tres-text-secondary">{value.uri}</span>;
  }
  // Literal
  return (
    <span>
      <span className="text-tres-text-primary">{value.value}</span>
      {value.lang && (
        <span className="ml-1 text-[10px] uppercase text-tres-text-secondary">@{value.lang}</span>
      )}
      {value.datatype && !value.lang && (
        <span
          className="ml-1 text-[10px] text-tres-text-secondary"
          title={value.datatype}
        >
          ({localPart(value.datatype)})
        </span>
      )}
    </span>
  );
}

function valueKey(v: PredicateValue): string {
  if (v.kind === 'uri' || v.kind === 'bnode') return v.uri;
  return `${v.value}|${v.lang ?? ''}|${v.datatype ?? ''}`;
}

function localPart(uri: string): string {
  const idx = Math.max(uri.lastIndexOf('#'), uri.lastIndexOf('/'));
  if (idx >= 0 && idx + 1 < uri.length) {
    return uri.slice(idx + 1);
  }
  return uri;
}

// ---- search ----

// sessionStorage so the in-progress search survives nav-tab switches while
// staying isolated per browser tab.
const SEARCH_STORAGE_KEY = 'entitySearch.entity';

function EntitySearch({
  graphs,
  onPick,
}: {
  graphs: string[];
  onPick: (r: VdbSearchResult) => void;
}) {
  const [text, setText] = useState(() => {
    try {
      return window.sessionStorage.getItem(SEARCH_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  useEffect(() => {
    try {
      if (text) window.sessionStorage.setItem(SEARCH_STORAGE_KEY, text);
      else window.sessionStorage.removeItem(SEARCH_STORAGE_KEY);
    } catch {
      // Storage unavailable (private mode) — search just won't survive navigation.
    }
  }, [text]);
  const debounced = useDebounced(text, 200);
  // Gate on the NORMALISED length: the hook strips a prefix marker, so `:ma`
  // is a two-character query and must not open an empty dropdown.
  const effectiveLen = searchTermFromInput(text).length;
  // The search endpoint takes no graph, so the scope is applied to the hits
  // here; a narrowed scope over-fetches so the visible list is still a full
  // page after filtering.
  const results = useVdbSearch(debounced, scopeNarrowsHits(graphs) ? 100 : 20);
  const list = useMemo(
    () => hitsWithinScope(results.data ?? [], graphs).slice(0, 20),
    [results.data, graphs],
  );

  return (
    <div className="relative">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Search entity by label (3+ chars)…"
        className="w-full rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {text && (
        <button
          type="button"
          onClick={() => setText('')}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'absolute right-2 top-1/2')}
          aria-label="Clear search"
        >
          ×
        </button>
      )}
      {effectiveLen >= 3 && (
        <div className="absolute z-10 mt-1 max-h-80 w-full overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          {results.isLoading && (
            <div className="px-3 py-2 text-xs text-tres-text-secondary">Searching…</div>
          )}
          {results.error && (
            <div className="px-3 py-2 text-xs text-tres-error">
              {(results.error as Error).message}
            </div>
          )}
          {!results.isLoading && !results.error && list.length === 0 && (
            <div className="px-3 py-3 text-center text-sm font-medium text-tres-text-primary">
              No entities match “{debounced}”.
              <div className="mt-0.5 text-xs font-normal text-tres-text-secondary">
                Check spelling, try fewer characters, or pick a different graph.
              </div>
            </div>
          )}
          {list.map((r) => (
            <button
              key={r.uri}
              type="button"
              onClick={() => {
                onPick(r);
                setText('');
              }}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-tres-text-primary">{r.label ?? localPart(r.uri)}</span>
                {r.classes.map((c) => (
                  <span
                    key={c.uri}
                    title={c.uri}
                    className="rounded-md bg-tres-menu-bg/10 px-1.5 py-0.5 text-[10px] font-medium text-tres-menu-bg"
                  >
                    {c.label}
                  </span>
                ))}
                <TagNameChips names={r.tag_names} />
              </div>
              <div className="truncate font-mono text-[10px] text-tres-text-secondary">
                {r.uri}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
